import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, State, StateContext } from '@ngxs/store';
import { firstValueFrom, from, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Account } from '../../../domain/auth/model/account';
import { AuthProvider } from '../../../domain/auth/model/auth-provider';
import { AuthToken } from '../../../domain/auth/model/auth-token';
import { AuthApiService } from '../../../domain/auth/service/auth-api.service';
import { AUTH_PROVIDER } from '../../common/injector-token';
import { NotificationUtil } from '../../util/notification.util';
import { AuthActions } from './auth.actions';

export interface AuthStateModel {
  username: string;

  token: AuthToken | null;

  isProcessing: boolean;

  account: Account | null;
}

@State<AuthStateModel>({
  name: 'auth',
})
@Injectable()
export class AuthState {
  constructor(
    @Inject(AUTH_PROVIDER) private readonly authProvider: AuthProvider,
    private readonly notificationUtil: NotificationUtil,
    private readonly authApiService: AuthApiService,
    private router: Router
  ) {}

  @Action(AuthActions.Login)
  async login(ctx: StateContext<AuthStateModel>, action: AuthActions.Login) {
    try {
      ctx.patchState({ isProcessing: true, username: action.username });

      const loginResult = await this.authProvider.login(
        action.username,
        action.password
      );

      if (loginResult.nextStep === 'DONE') {
        const token = await this.authProvider.generateToken();
        await firstValueFrom(ctx.dispatch(new AuthActions.SaveToken(token)));
      } else {
        throw new Error(loginResult.nextStep);
      }
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.LoginWithGoogle)
  async loginWithGoogle(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.LoginWithGoogle
  ) {
    try {
      const loginResult = await this.authProvider.loginWithGoogle();
      if (loginResult.nextStep !== 'DONE') {
        throw new Error(loginResult.nextStep);
      }
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.LoginWithFacebook)
  async loginWithFacebok(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.LoginWithFacebook
  ) {
    try {
      const loginResult = await this.authProvider.loginWithFacebook();

      if (loginResult.nextStep !== 'DONE') {
        throw new Error(loginResult.nextStep);
      }
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.ConfirmSocialLogin)
  async confirmSocialLogin(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.ConfirmSocialLogin
  ) {
    try {
      const token = await this.authProvider.generateToken();
      await firstValueFrom(ctx.dispatch(new AuthActions.SaveToken(token)));
      this.router.navigate(['/']);
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.SetPassword)
  async setPassword(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetPassword
  ) {
    try {
      ctx.patchState({ isProcessing: true });
      await this.authProvider.setPassword(action.password);
      const token = await this.authProvider.generateToken();
      await firstValueFrom(ctx.dispatch(new AuthActions.SaveToken(token)));
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.ChangePassword)
  async changePassword(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.ChangePassword
  ) {
    try {
      ctx.patchState({ isProcessing: true });
      await this.authProvider.changePassword(
        action.currentPassword,
        action.password
      );
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.ConfirmResetPassword)
  async confirmResetPassword(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.ConfirmResetPassword
  ) {
    try {
      const { username, code, password } = action;
      ctx.patchState({ isProcessing: true });
      await this.authProvider.confirmResetPassword(username, code, password);
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.ResetPassword)
  async resetPassword(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.ResetPassword
  ) {
    try {
      ctx.patchState({ isProcessing: true });
      await this.authProvider.resetPassword(action.username);
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    } finally {
      ctx.patchState({ isProcessing: false });
    }
  }

  @Action(AuthActions.RefreshToken)
  async refreshToken(ctx: StateContext<AuthStateModel>) {
    return from(this.authProvider.refresh()).pipe(
      tap((refreshedToken: AuthToken) =>
        ctx.dispatch(new AuthActions.SaveToken(refreshedToken))
      ),
      catchError(() => {
        this.notificationUtil.info('MESSAGE.REFRESH_TOKEN_EXPIRED');
        return ctx.dispatch(new AuthActions.Logout());
      })
    );
  }

  @Action(AuthActions.Logout)
  async logout(ctx: StateContext<AuthStateModel>) {
    return this.authProvider.logout().then(() => {
      ctx.patchState({ token: null, account: null });
    });
  }

  @Action(AuthActions.SaveToken)
  async saveToken(
    ctx: StateContext<AuthStateModel>,
    payload: AuthActions.SaveToken
  ) {
    const { token } = payload;
    ctx.patchState({ token: token, isProcessing: false });
    await firstValueFrom(ctx.dispatch(new AuthActions.GetCurrentLogged()));
  }

  @Action(AuthActions.GetCurrentLogged)
  getCurrentLogged(ctx: StateContext<AuthStateModel>) {
    return this.authApiService
      .getCurrentLoggedUser()
      .pipe(tap((account: Account) => ctx.patchState({ account })));
  }
}
